import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { IoMdClose } from 'react-icons/io';
import {
  FiArrowLeft,
  FiChevronRight,
  FiCloud,
  FiFileText,
  FiFolder,
  FiGlobe,
  FiSearch,
} from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import { forgetSharePointPlaceNames } from '../../hooks/useSharePointPlaceNames';
import SharePointAccessModal from './SharePointAccessModal';
import {
  addSharePointSite,
  getSharePointConnection,
  listAvailableSharePointSites,
  listSharePointFiles,
  removeSharePointSite,
} from '../../services/api';

const errorText = (err) =>
  (typeof err === 'object' && err?.message) ||
  String(err || 'Something went wrong');

// One place at a time: a turned-on site or OneDrive, browsed folder by folder.
const FileBrowser = ({ token, place, onBack }) => {
  const [trail, setTrail] = useState([]); // [{id, name}] of opened folders
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const folderId = trail.length ? trail[trail.length - 1].id : undefined;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listSharePointFiles(token, place.id, folderId)
      .then((res) => !cancelled && setItems(res?.items || []))
      .catch((err) => !cancelled && setError(errorText(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, place.id, folderId]);

  return (
    <div>
      <button
        onClick={trail.length ? () => setTrail(trail.slice(0, -1)) : onBack}
        className="mb-2 flex items-center gap-1 text-sm text-blue-400"
      >
        <FiArrowLeft /> {trail.length ? 'Up' : 'All sites'}
      </button>
      <p className="mb-2 text-sm text-zinc-300">
        {[place.name, ...trail.map((t) => t.name)].join(' / ')}
      </p>
      {loading && <p className="text-sm text-zinc-400">Loading…</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}
      {!loading && !error && !items.length && (
        <p className="text-sm text-zinc-400">
          No folders or files of the chosen types here.
        </p>
      )}
      <ul className="divide-y divide-zinc-800">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-2 py-2 text-sm text-zinc-200"
          >
            {item.is_folder ? (
              <button
                onClick={() =>
                  setTrail([...trail, { id: item.id, name: item.name }])
                }
                className="flex items-center gap-2 hover:text-blue-400"
              >
                <FiFolder /> {item.name}
              </button>
            ) : (
              <a
                href={item.web_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 hover:text-blue-400"
              >
                <FiFileText /> {item.name}
              </a>
            )}
            {item.modified_at && (
              <span className="ml-auto text-xs text-zinc-500">
                {new Date(item.modified_at).toLocaleDateString()}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Switch = ({ on, busy, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={busy}
    onClick={onToggle}
    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
      on ? 'bg-blue-600' : 'bg-zinc-600'
    } ${busy ? 'opacity-50' : ''}`}
  >
    <span
      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
        on ? 'left-[18px]' : 'left-0.5'
      }`}
    />
  </button>
);

const SharePointSitesModal = ({ onClose, onChangeAccess }) => {
  const { userGoogleId } = DataState();
  const [connection, setConnection] = useState(null);
  const [sites, setSites] = useState(null); // available sites with `enabled`
  const [search, setSearch] = useState('');
  const [sitesError, setSitesError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [place, setPlace] = useState(null);
  const [changingAccess, setChangingAccess] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Which sites sort first: those on when the panel loaded. Toggling doesn't
  // re-sort, so a row never jumps out from under the pointer.
  const [sortOn, setSortOn] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const conn = await getSharePointConnection(userGoogleId);
      setConnection(conn);
      setSortOn(new Set((conn?.sites || []).map((s) => s.id)));
    } catch (err) {
      setError(errorText(err));
    }
  }, [userGoogleId]);

  // Escape closes the panel (the access dialog closes itself first).
  useEffect(() => {
    if (changingAccess) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [changingAccess, onClose]);

  const loadSites = useCallback(
    async (term) => {
      setSitesError('');
      try {
        const res = await listAvailableSharePointSites(userGoogleId, term);
        setSites(res?.sites || []);
      } catch (err) {
        setSites([]);
        setSitesError(errorText(err));
      }
    },
    [userGoogleId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const sitesOn = Boolean(connection?.connected && connection?.access?.sites);

  // Search as the user types, after a short pause.
  useEffect(() => {
    if (!sitesOn) return undefined;
    const timer = setTimeout(() => loadSites(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [sitesOn, search, loadSites]);

  const markEnabled = (siteId, enabled) =>
    setSites((list) =>
      (list || []).map((s) => (s.id === siteId ? { ...s, enabled } : s)),
    );

  const toggleSite = async (site) => {
    setBusyId(site.id);
    setError('');
    try {
      const res = site.enabled
        ? await removeSharePointSite(userGoogleId, site.id)
        : await addSharePointSite(userGoogleId, { siteId: site.id });
      setConnection((c) => ({ ...c, sites: res.sites }));
      markEnabled(site.id, !site.enabled);
      forgetSharePointPlaceNames();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusyId('');
    }
  };

  const handlePaste = async () => {
    if (!url.trim()) {
      setError('Paste a SharePoint link');
      return;
    }
    setBusyId('paste');
    setError('');
    try {
      const res = await addSharePointSite(userGoogleId, { url: url.trim() });
      setConnection((c) => ({ ...c, sites: res.sites }));
      markEnabled(res.site?.id, true);
      forgetSharePointPlaceNames();
      setUrl('');
      setShowPaste(false);
      toast.success(`Turned on ${res.site?.name || 'site'}`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusyId('');
    }
  };

  // Always through Microsoft: it asks only for what's new, and returns at
  // once when nothing new is asked for. Turned-on sites stay.
  const handleChangeAccess = async (newAccess) => {
    setChangingAccess(false);
    setUpdating(true);
    setError('');
    try {
      await onChangeAccess(newAccess);
      await load();
      // The new grant may open sites that were refused before.
      setSites(null);
      await loadSites(search.trim());
    } finally {
      setUpdating(false);
    }
  };

  const access = connection?.access || {};
  const enabledIds = new Set((connection?.sites || []).map((s) => s.id));
  // Turned-on sites first, then the rest; a turned-on site missing from the
  // search results still shows.
  const listed = [
    ...(connection?.sites || [])
      .filter((s) => !(sites || []).some((a) => a.id === s.id))
      .filter((s) =>
        search.trim()
          ? (s.name || '').toLowerCase().includes(search.trim().toLowerCase())
          : true,
      )
      .map((s) => ({ ...s, enabled: true })),
    ...(sites || []).map((s) => ({ ...s, enabled: enabledIds.has(s.id) })),
  ].sort((a, b) => Number(sortOn.has(b.id)) - Number(sortOn.has(a.id)));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-1 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-y-auto rounded-xl border border-zinc-700 p-6">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/icons/onedrive_icon.png"
              alt=""
              className="h-9 w-9 rounded-lg bg-zinc-200 p-1.5"
            />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                SharePoint
              </h2>
              {connection?.connected && (
                <>
                  {connection.account?.email && (
                    <p className="text-xs text-zinc-300">
                      Connected as {connection.account.email}
                    </p>
                  )}
                  <p className="text-xs text-zinc-400">
                    {access.write ? 'Read and edit' : 'Read only'} ·{' '}
                    {(access.file_types || []).join(', ') || 'no file types'}
                    {access.onenote ? ', OneNote' : ''}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connection?.connected && onChangeAccess && (
              <button
                onClick={() => setChangingAccess(true)}
                disabled={updating}
                className="rounded-md border border-zinc-600 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800"
              >
                {updating ? 'Updating…' : 'Change access'}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <IoMdClose size={20} />
            </button>
          </div>
        </div>

        {!connection && !error && (
          <p className="text-sm text-zinc-400">Loading…</p>
        )}
        {connection && !connection.connected && (
          <p className="text-sm text-zinc-400">Connect SharePoint first.</p>
        )}
        {connection?.missing_scopes?.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-800/50 bg-amber-900/20 p-3 text-sm text-amber-300">
            Microsoft hasn&apos;t granted everything your choices need (
            {connection.missing_scopes.join(', ')}), so some actions will be
            refused. Reconnect: click <strong>Change access</strong>, then{' '}
            <strong>Continue to Microsoft</strong>.
          </div>
        )}

        {connection?.connected && place && (
          <FileBrowser
            token={userGoogleId}
            place={place}
            onBack={() => setPlace(null)}
          />
        )}

        {connection?.connected && !place && (
          <>
            {access.onedrive && (
              <button
                onClick={() =>
                  setPlace({ id: 'onedrive', name: 'My OneDrive' })
                }
                className="mb-4 flex w-full items-center gap-3 rounded-lg border border-zinc-700 p-3 text-left hover:border-zinc-500"
              >
                <FiCloud className="text-blue-400" aria-hidden="true" />
                <span className="flex-1 text-sm text-zinc-100">
                  My OneDrive
                </span>
                <FiChevronRight className="text-zinc-500" aria-hidden="true" />
              </button>
            )}

            {sitesOn && (
              <section>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Your SharePoint sites
                  </h3>
                  <span className="text-xs text-zinc-500">
                    Turn on the sites Oqlous may use
                  </span>
                </div>
                <div className="mb-2 flex items-center gap-2 rounded-md border border-zinc-600 px-3">
                  <FiSearch className="text-zinc-500" aria-hidden="true" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search sites"
                    aria-label="Search sites"
                    className="w-full bg-transparent py-2 text-sm text-zinc-200 outline-none"
                  />
                </div>

                {sites === null && (
                  <p className="py-2 text-sm text-zinc-400">Loading sites…</p>
                )}
                {sitesError && (
                  <p className="py-2 text-sm text-red-400">{sitesError}</p>
                )}
                {sites !== null && !sitesError && !listed.length && (
                  <p className="py-2 text-sm text-zinc-400">No sites found.</p>
                )}

                <ul className="divide-y divide-zinc-800">
                  {listed.map((site) => (
                    <li key={site.id} className="flex items-center gap-3 py-2">
                      <FiGlobe
                        className={
                          site.enabled ? 'text-blue-400' : 'text-zinc-500'
                        }
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-zinc-100">
                          {site.name}
                        </p>
                        {site.web_url && (
                          <p className="truncate text-xs text-zinc-500">
                            {site.web_url}
                          </p>
                        )}
                      </div>
                      {site.enabled && (
                        <button
                          onClick={() => setPlace(site)}
                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                        >
                          Browse <FiChevronRight aria-hidden="true" />
                        </button>
                      )}
                      <Switch
                        on={site.enabled}
                        busy={busyId === site.id}
                        onToggle={() => toggleSite(site)}
                        label={`${site.enabled ? 'Turn off' : 'Turn on'} ${site.name}`}
                      />
                    </li>
                  ))}
                </ul>

                <div className="mt-3">
                  {!showPaste ? (
                    <button
                      onClick={() => setShowPaste(true)}
                      className="text-xs text-zinc-400 underline hover:text-zinc-200"
                    >
                      Can't find a site? Paste a link to it
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                          setError('');
                        }}
                        placeholder="https://contoso.sharepoint.com/sites/Project"
                        aria-label="SharePoint link"
                        className="flex-1 rounded-md border border-zinc-600 bg-transparent px-3 py-2 text-sm text-zinc-200"
                      />
                      <button
                        onClick={handlePaste}
                        disabled={busyId === 'paste'}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                      >
                        {busyId === 'paste' ? 'Checking…' : 'Turn on'}
                      </button>
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}

        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

        {changingAccess && (
          <SharePointAccessModal
            title="Change SharePoint access"
            initialAccess={access}
            onContinue={handleChangeAccess}
            onClose={() => setChangingAccess(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SharePointSitesModal;
